import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Webpack breaks Leaflet's default icon URL detection; point it at bundled assets.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [44.6488, -63.5752]; // Halifax, NS
const DEFAULT_ZOOM = 11;

const hasCoords = (cell) =>
    cell.latitude != null && cell.longitude != null &&
    Number.isFinite(Number(cell.latitude)) && Number.isFinite(Number(cell.longitude));

// Fit the map to the visible markers whenever they change.
const FitBounds = ({ positions }) => {
    const map = useMap();
    useEffect(() => {
        if (positions.length === 1) {
            map.setView(positions[0], 14);
        } else if (positions.length > 1) {
            map.fitBounds(positions, { padding: [40, 40], maxZoom: 15 });
        }
    }, [map, positions]);
    return null;
};

const CellMap = ({ cells, height = 480, onGeocodeMissing, geocoding, onEditCell }) => {
    const located = useMemo(() => cells.filter(hasCoords), [cells]);
    const unlocated = useMemo(() => cells.filter((c) => !hasCoords(c)), [cells]);
    const missing = unlocated.length;
    const positions = useMemo(
        () => located.map((c) => [Number(c.latitude), Number(c.longitude)]),
        [located]
    );

    return (
        <div>
            {missing > 0 && (
                <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                        {missing} of {cells.length} cell{cells.length === 1 ? '' : 's'} can't be shown because the address couldn't be located.
                        Check the address and city, then save the cell again.
                    </span>
                    {onGeocodeMissing && (
                        <button
                            type="button"
                            onClick={onGeocodeMissing}
                            disabled={geocoding}
                            className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                        >
                            {geocoding ? 'Locating…' : 'Locate missing cells'}
                        </button>
                    )}
                </div>
                <ul aria-label="Cells not on the map" className="mt-2 space-y-1">
                    {unlocated.map((cell) => {
                        const where = [cell.address, cell.city].filter((v) => v && String(v).trim()).join(' · ');
                        return (
                            <li key={cell.id} className="flex flex-wrap items-baseline gap-x-2">
                                {onEditCell ? (
                                    <button
                                        type="button"
                                        onClick={() => onEditCell(cell)}
                                        className="font-semibold underline underline-offset-2 hover:text-amber-900"
                                        aria-label={`Edit ${cell.name}`}
                                    >
                                        {cell.name}
                                    </button>
                                ) : (
                                    <span className="font-semibold">{cell.name}</span>
                                )}
                                <span className="text-amber-700">{where || 'No address or city'}</span>
                            </li>
                        );
                    })}
                </ul>
                </div>
            )}
            <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                scrollWheelZoom
                style={{ height, width: '100%', zIndex: 0 }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={positions} />
                {located.map((cell) => (
                    <Marker key={cell.id} position={[Number(cell.latitude), Number(cell.longitude)]}>
                        <Popup>
                            <div className="space-y-0.5">
                                <p className="font-semibold">{cell.name}</p>
                                <p>{cell.address}{cell.city ? `, ${cell.city}` : ''}</p>
                                {cell.CellLeader?.name && <p>Leader: {cell.CellLeader.name}</p>}
                                {(cell.meeting_day || cell.meeting_time) && (
                                    <p>{[cell.meeting_day, cell.meeting_time].filter(Boolean).join(' · ')}</p>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default CellMap;
